import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Lock, AlertTriangle, Check, Loader } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import { authService } from '../services/authService';
import UnifiedHeader from '../components/UnifiedHeader';
import './Settings.css';

export default function Settings() {
  const navigate = useNavigate();
  const { language, setLanguage } = useLanguage();
  const t = translations[language]?.settings || {};

  const token = authService.getToken();
  const cachedUser = authService.getUser();

  // Profile state
  const [fullName, setFullName] = useState('');
  const [preferredLanguage, setPreferredLanguage] = useState(language);
  const [isGoogleAccount, setIsGoogleAccount] = useState(false);
  const [profileStatus, setProfileStatus] = useState(null); // null | 'saving' | 'saved' | 'error'
  const [profileError, setProfileError] = useState('');

  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordStatus, setPasswordStatus] = useState(null); // null | 'updating' | 'updated' | 'error'
  const [passwordError, setPasswordError] = useState('');

  // Delete state
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteStatus, setDeleteStatus] = useState(null); // null | 'deleting' | 'error'
  const [deleteError, setDeleteError] = useState('');
  const [showDeleteSection, setShowDeleteSection] = useState(false);

  // Load profile
  useEffect(() => {
    if (cachedUser) {
      setFullName(cachedUser.fullName || '');
      setPreferredLanguage(cachedUser.preferredLanguage || language);
    }
    authService.getProfile(token)
      .then(data => {
        setFullName(data.user.fullName || '');
        setPreferredLanguage(data.user.preferredLanguage || language);
        setIsGoogleAccount(data.user.isGoogleAccount || false);
      })
      .catch(() => {}); // silently fall back to cached
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveProfile = async () => {
    setProfileError('');
    setProfileStatus('saving');
    try {
      const result = await authService.updateProfile(token, { fullName, preferredLanguage });
      // Update localStorage so other components reflect the change
      const stored = authService.getUser() || {};
      authService.saveAuth(token, { ...stored, fullName: result.user.fullName, preferredLanguage: result.user.preferredLanguage });
      // Switch app language immediately
      if (result.user.preferredLanguage !== language) {
        setLanguage(result.user.preferredLanguage);
      }
      setProfileStatus('saved');
      setTimeout(() => setProfileStatus(null), 2500);
    } catch (err) {
      setProfileError(err.message);
      setProfileStatus('error');
    }
  };

  const handleChangePassword = async () => {
    setPasswordError('');
    if (newPassword !== confirmPassword) {
      setPasswordError(t.passwordMismatch || 'Passwords do not match');
      return;
    }
    if (newPassword.length < 8 || !/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(newPassword)) {
      setPasswordError(t.passwordTooWeak || 'Min 8 chars with uppercase, lowercase and number');
      return;
    }
    setPasswordStatus('updating');
    try {
      await authService.changePassword(token, currentPassword, newPassword);
      setPasswordStatus('updated');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => setPasswordStatus(null), 2500);
    } catch (err) {
      setPasswordError(err.message);
      setPasswordStatus('error');
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirm !== 'DELETE') return;
    setDeleteError('');
    setDeleteStatus('deleting');
    try {
      await authService.deleteAccount(token);
      localStorage.clear();
      sessionStorage.clear();
      navigate('/', { replace: true });
    } catch (err) {
      setDeleteError(err.message);
      setDeleteStatus('error');
    }
  };

  const memberSince = cachedUser?.createdAt
    ? new Date(cachedUser.createdAt).toLocaleDateString(
        language === 'fi' ? 'fi-FI' : language === 'sv' ? 'sv-SE' : 'en-GB',
        { year: 'numeric', month: 'long' }
      )
    : null;

  return (
    <div className="settings-page">
      <UnifiedHeader />

      <div className="settings-container">
        {/* Page header */}
        <div className="settings-header">
          <button className="settings-back-btn" onClick={() => navigate(-1)}>
            <ArrowLeft size={18} />
            {t.back || 'Back'}
          </button>
          <div>
            <h1 className="settings-title">{t.title || 'Account Settings'}</h1>
            {memberSince && (
              <p className="settings-member-since">
                {t.memberSince || 'Member since'} {memberSince}
              </p>
            )}
          </div>
        </div>

        {/* ── Profile ── */}
        <section className="settings-card">
          <div className="settings-card-header">
            <div className="settings-card-icon">
              <User size={20} />
            </div>
            <h2 className="settings-card-title">{t.profileSection || 'Profile'}</h2>
          </div>

          <div className="settings-field">
            <label className="settings-label">{t.fullName || 'Full Name'}</label>
            <input
              className="settings-input"
              type="text"
              value={fullName}
              onChange={e => setFullName(e.target.value)}
              disabled={profileStatus === 'saving'}
            />
          </div>

          <div className="settings-field">
            <label className="settings-label">{t.language || 'Language'}</label>
            <select
              className="settings-select"
              value={preferredLanguage}
              onChange={e => setPreferredLanguage(e.target.value)}
              disabled={profileStatus === 'saving'}
            >
              <option value="fi">Suomi</option>
              <option value="sv">Svenska</option>
              <option value="en">English</option>
            </select>
          </div>

          {profileError && <p className="settings-error">{profileError}</p>}

          <button
            className="settings-btn settings-btn-primary"
            onClick={handleSaveProfile}
            disabled={profileStatus === 'saving'}
          >
            {profileStatus === 'saving' && <Loader size={16} className="settings-spin" />}
            {profileStatus === 'saved' && <Check size={16} />}
            {profileStatus === 'saving'
              ? (t.saving || 'Saving...')
              : profileStatus === 'saved'
              ? (t.saved || 'Saved!')
              : (t.saveChanges || 'Save Changes')}
          </button>
        </section>

        {/* ── Change Password ── */}
        <section className="settings-card">
          <div className="settings-card-header">
            <div className="settings-card-icon">
              <Lock size={20} />
            </div>
            <h2 className="settings-card-title">{t.passwordSection || 'Change Password'}</h2>
          </div>

          {isGoogleAccount ? (
            <p className="settings-muted">{t.googleAccount || 'Google account — password change not available'}</p>
          ) : (
            <>
              <div className="settings-field">
                <label className="settings-label">{t.currentPassword || 'Current Password'}</label>
                <input
                  className="settings-input"
                  type="password"
                  value={currentPassword}
                  onChange={e => setCurrentPassword(e.target.value)}
                  disabled={passwordStatus === 'updating'}
                  autoComplete="current-password"
                />
              </div>
              <div className="settings-field">
                <label className="settings-label">{t.newPassword || 'New Password'}</label>
                <input
                  className="settings-input"
                  type="password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  disabled={passwordStatus === 'updating'}
                  autoComplete="new-password"
                />
              </div>
              <div className="settings-field">
                <label className="settings-label">{t.confirmPassword || 'Confirm New Password'}</label>
                <input
                  className="settings-input"
                  type="password"
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  disabled={passwordStatus === 'updating'}
                  autoComplete="new-password"
                />
              </div>

              {passwordError && <p className="settings-error">{passwordError}</p>}

              <button
                className="settings-btn settings-btn-primary"
                onClick={handleChangePassword}
                disabled={passwordStatus === 'updating' || !currentPassword || !newPassword || !confirmPassword}
              >
                {passwordStatus === 'updating' && <Loader size={16} className="settings-spin" />}
                {passwordStatus === 'updated' && <Check size={16} />}
                {passwordStatus === 'updating'
                  ? (t.updating || 'Updating...')
                  : passwordStatus === 'updated'
                  ? (t.passwordUpdated || 'Password updated!')
                  : (t.updatePassword || 'Update Password')}
              </button>
            </>
          )}
        </section>

        {/* ── Danger Zone ── */}
        <section className="settings-card settings-card-danger">
          <div className="settings-card-header">
            <div className="settings-card-icon settings-card-icon-danger">
              <AlertTriangle size={20} />
            </div>
            <h2 className="settings-card-title settings-card-title-danger">
              {t.dangerZone || 'Danger Zone'}
            </h2>
          </div>

          {!showDeleteSection ? (
            <div className="settings-danger-preview">
              <div>
                <p className="settings-danger-label">{t.deleteAccount || 'Delete Account'}</p>
                <p className="settings-muted">{t.deleteWarning || 'Permanently deletes your account and all data.'}</p>
              </div>
              <button
                className="settings-btn settings-btn-danger-outline"
                onClick={() => setShowDeleteSection(true)}
              >
                {t.deleteAccount || 'Delete Account'}
              </button>
            </div>
          ) : (
            <>
              <p className="settings-warning-text">{t.deleteWarning || 'This permanently deletes your account and all your data. This cannot be undone.'}</p>

              <div className="settings-field">
                <label className="settings-label">{t.deleteConfirmLabel || 'Type DELETE to confirm'}</label>
                <input
                  className="settings-input settings-input-danger"
                  type="text"
                  value={deleteConfirm}
                  onChange={e => setDeleteConfirm(e.target.value)}
                  placeholder={t.deleteConfirmPlaceholder || 'Type DELETE here'}
                  disabled={deleteStatus === 'deleting'}
                />
              </div>

              {deleteError && <p className="settings-error">{deleteError}</p>}

              <div className="settings-danger-actions">
                <button
                  className="settings-btn settings-btn-ghost"
                  onClick={() => { setShowDeleteSection(false); setDeleteConfirm(''); setDeleteError(''); }}
                  disabled={deleteStatus === 'deleting'}
                >
                  Cancel
                </button>
                <button
                  className="settings-btn settings-btn-danger"
                  onClick={handleDeleteAccount}
                  disabled={deleteConfirm !== 'DELETE' || deleteStatus === 'deleting'}
                >
                  {deleteStatus === 'deleting' && <Loader size={16} className="settings-spin" />}
                  {deleteStatus === 'deleting'
                    ? (t.deleting || 'Deleting...')
                    : (t.deleteConfirmBtn || 'Permanently Delete My Account')}
                </button>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
